/* eslint-disable camelcase */
import * as THREE from 'three'
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader'
import {
  calculateMeshCountInsideObject,
  getSeatMaterial
} from '../utils/helper_functions'
import { drawLabel } from './drawLabel'
import { seats, textGeometries, materials } from '../utils/mapData'
import { OBJECT_TYPES, COLORS, SEAT_STATUSES } from '../constants'

const loadPromise = (loader, url) => {
  return new Promise((resolve, reject) => {
    loader.load(url, resolve, undefined, reject)
  })
}

const createBorderedMesh = (geometry, material) => {
  const edges = new THREE.EdgesGeometry(geometry)
  const mesh = new THREE.LineSegments(edges, material)
  return mesh
}

export const drawMapWithSVG = async (
  group,
  groups,
  options,
  predefinedSeats
) => {
  const {
    scene,
    scale,
    events,
    seat_circle_radius,
    fonts,
    svgData,
    sceneMeshes,
    statuses
  } = options

  const svgLoader = new SVGLoader()

  const getSvgSeatId = (svgNode) => {
    let svgSeatId = svgNode.id
    if (!svgSeatId) {
      svgSeatId = getSvgSeatId(svgNode.parentElement)
    }

    return svgSeatId
  }

  try {
    const id = calculateMeshCountInsideObject(scene)
    const data = await loadPromise(svgLoader, svgData.elementsUrl)
    const paths = data.paths
    paths.forEach((path) => {
      const svgSeatId = getSvgSeatId(path.userData.node)
      const isTextEl = !!path.userData.node.attributes['data-istext']
      const isBlockEl = !!path.userData.node.attributes['data-isblock']
      // Next ine is not bad but temporary solution. I think that will be good to integrate calculation system for renderOrder logic
      const isCoverAll = !!path.userData.node.attributes.iscoverall
      const isColorChangable = !path.userData.node.attributes.unchangableColor
      const isBorderedShape = !!path.userData.node.attributes.borderedShape
      const isFrameShape = !!path.userData.node.attributes.frameShape
      const borderColor = path.userData.node.attributes.borderColor || 'black'
      let jsonData = null
      const groupsArr = Object.values(groups)
      for (let i = 0; i < groupsArr.length; ++i) {
        const group = groupsArr[i]
        const rowArr = Object.values(group.groups)
        for (let j = 0; j < rowArr.length; ++j) {
          const row = rowArr[j]
          const x = row.units.find((el) => el.svgId === svgSeatId)
          if (x) {
            jsonData = x
            break
          }
        }
        if (jsonData) {
          break
        }
      }
      const { tier: tierId, label, id: seatId } = jsonData
      const [groupRowId, seatIndex] = seatId.split(':')
      const seatStatus = statuses[groupRowId][seatIndex]
      const color =
        isColorChangable &&
        predefinedSeats &&
        predefinedSeats.includes(seatId) &&
        seatStatus === SEAT_STATUSES.AVAILABLE
          ? COLORS.PREDEFINED
          : path.color.getHex() || 'black'

      const material = getSeatMaterial(
        {
          color,
          options: {
            side: THREE.DoubleSide,
            depthWrite: false
          }
        },
        materials
      )
      const obj = new THREE.Group()
      const shapes = path.toShapes(true)
      shapes.forEach((shape) => {
        const geometry = new THREE.ShapeGeometry(shape)

        const mesh = new THREE.Mesh(geometry, material)
        const mesh1 =
          isBorderedShape || isFrameShape
            ? createBorderedMesh(geometry, material)
            : null
        mesh.rotateY(Math.PI)
        mesh.rotateZ(Math.PI)

        !isBlockEl && !isFrameShape && obj.add(mesh)
        if (!isFrameShape) {
          sceneMeshes.push(mesh)
        }

        if (mesh1) {
          !isBlockEl && obj.add(mesh1)
          sceneMeshes.push(mesh1)
        }

        mesh.sgId = id
        mesh.isRequestPending = false
        mesh.objectType = OBJECT_TYPES.SEAT
        mesh.allowColorUpdateByStatus = !isTextEl
        mesh.seatId = seatId
        mesh.tierId = tierId
        mesh.status = seatStatus
        mesh.label = label
        mesh.hasHandlers = true
        mesh.isColorChangable = isColorChangable
        mesh.mouseOver = events.onMouseOver
        mesh.mouseOut = events.onMouseOut
        mesh.changeBorderColor = (color) => {}
        mesh.changeBorderColorToDefault = () => {}
        let renderOrder = 100
        renderOrder =
          isTextEl || !isColorChangable ? 300 : isBlockEl ? 500 : 100
        if (isCoverAll) {
          renderOrder = 500
        }
        mesh.renderOrder = renderOrder
        mesh.getNeighbourSeats = () => seats[seatId]
        mesh.changeColor = (color) => {
          if (isTextEl || isBlockEl) {
            return
          }
          mesh.material = getSeatMaterial(
            {
              color,
              options: {
                side: THREE.DoubleSide,
                depthWrite: false
              }
            },
            materials
          )
        }

        if (mesh1) {
          mesh1.rotateY(Math.PI)
          mesh1.rotateZ(Math.PI)
          mesh1.renderOrder = renderOrder + 1
          mesh1.changeColor = (color) => {
            mesh1.material = getSeatMaterial(
              {
                color,
                options: {
                  side: THREE.DoubleSide,
                  depthWrite: false
                }
              },
              materials
            )
          }
          mesh1.changeColor(borderColor)
        }

        !(isTextEl || isBlockEl || !isColorChangable) &&
          mesh.changeColor(svgData.booked_color || COLORS.BOOKED)

        const makeAvailable = () => {
          mesh.status = SEAT_STATUSES.AVAILABLE
        }

        const makeHold = () => {
          mesh.status = SEAT_STATUSES.HOLD
        }

        const makeReserved = () => {
          mesh.status = SEAT_STATUSES.RESERVED
        }

        const makeBooked = () => {
          mesh.status = SEAT_STATUSES.BOOKED
        }

        const makeBlocked = () => {
          mesh.status = SEAT_STATUSES.BLOCKED
        }

        const makeReservedByMe = () => {
          window.localStorage.setItem('tierId', tierId)
          mesh.status = SEAT_STATUSES.RESERVED_BY_ME
        }

        mesh.setColorByStatus = () => {
          if (!mesh.allowColorUpdateByStatus) {
            return
          }

          switch (mesh.status) {
            case SEAT_STATUSES.AVAILABLE: {
              mesh.changeColor(color)
              break
            }
            case SEAT_STATUSES.HOLD: {
              mesh.changeColor(COLORS.HOLD)
              break
            }
            case SEAT_STATUSES.RESERVED_BY_ME: {
              mesh.changeColor(
                svgData.own_reservation_color || COLORS.RESERVED_BY_ME
              )
              break
            }
            case SEAT_STATUSES.RESERVED: {
              mesh.changeColor(svgData.reservation_color || COLORS.RESERVED)
              break
            }
            case SEAT_STATUSES.BOOKED: {
              mesh.changeColor(svgData.booked_color || COLORS.BOOKED)
              break
            }
            case SEAT_STATUSES.BLOCKED: {
              mesh.changeColor(COLORS.BLOCKED)
              break
            }
          }
        }

        if (isColorChangable) {
          mesh.setColorByStatus()
        }

        mesh.updateOpacity = () => {
          mesh.changeColor(`#${mesh.material.color.getHexString()}`)
        }

        mesh.makeDisabled = () => {
          if (mesh.disabled) {
            return
          }
          mesh.disabled = true
          mesh.mouseOver = null
          mesh.mouseOut = null
          mesh.opacity = 0.3
          mesh.updateOpacity()
        }

        mesh.makeActive = () => {
          if (!mesh.disabled) {
            return
          }
          mesh.disabled = false
          mesh.mouseOver = events.onMouseOver
          mesh.mouseOut = events.onMouseOut
          mesh.opacity = 1
          mesh.updateOpacity()
        }

        const select = (id) => {
          sceneMeshes.forEach((meshItem) => {
            if (
              meshItem.type === 'Mesh' &&
              id === mesh.label &&
              mesh.label === meshItem.label &&
              meshItem.isColorChangable
            ) {
              meshItem.material = getSeatMaterial(
                {
                  color: COLORS.HIGHLIGHT,
                  options: {
                    side: THREE.DoubleSide,
                    depthWrite: false
                  }
                },
                materials
              )
            }
          })
        }

        const unselect = (id) => {
          sceneMeshes.forEach((meshItem) => {
            if (
              meshItem.type === 'Mesh' &&
              id === mesh.label &&
              mesh.label === meshItem.label &&
              meshItem.isColorChangable
            ) {
              meshItem.material = getSeatMaterial(
                {
                  color,
                  options: {
                    side: THREE.DoubleSide,
                    depthWrite: false
                  }
                },
                materials
              )
            }
          })
        }

        mesh.select = select
        mesh.unselect = unselect

        mesh.unsetLoading = () => null
        mesh.setLoading = () => {
          if (window.isMobile) {
            // is mobile
            const label = drawLabel(
              '\uF110',
              {
                font: fonts.fontAwesome,
                color: 'grey',
                position: [0, 0, 0],
                center_x: 0,
                center_y: 0,
                scale,
                seat_circle_radius,
                scene: mesh,
                size: seat_circle_radius * scale
              },
              textGeometries,
              materials
            )
            mesh.unsetLoading = () => mesh.remove(label)
          }
        }

        mesh.statusUpdate = (status) => {
          if (mesh.status !== status) {
            switch (status) {
              case SEAT_STATUSES.PERMANENT_RESERVED:
              case SEAT_STATUSES.AVAILABLE: {
                makeAvailable()
                mesh.unsetLoading()
                isBlockEl && obj.remove(mesh)
                break
              }
              case SEAT_STATUSES.HOLD: {
                makeHold()
                mesh.unsetLoading()
                isBlockEl && obj.remove(mesh)
                break
              }
              case SEAT_STATUSES.RESERVED_BY_ME: {
                makeReservedByMe()
                mesh.unsetLoading()
                isBlockEl && obj.remove(mesh)
                break
              }
              case SEAT_STATUSES.RESERVED: {
                makeReserved()
                mesh.unsetLoading()
                isBlockEl && obj.remove(mesh)
                break
              }
              case SEAT_STATUSES.BOOKED: {
                makeBooked()
                mesh.unsetLoading()
                isBlockEl && obj.remove(mesh)
                break
              }
              case SEAT_STATUSES.BLOCKED: {
                makeBlocked()
                mesh.unsetLoading()
                isBlockEl && obj.add(mesh)
                break
              }
            }
            if (isColorChangable) {
              mesh.setColorByStatus()
            }
          }
        }

        if (seats[seatId]) {
          if (Array.isArray(seats[seatId])) {
            seats[seatId].push(mesh)
          } else {
            seats[seatId] = [seats[seatId], mesh]
          }
        } else {
          seats[seatId] = mesh
        }
      })
      obj.scale.set(scale * 1.3, scale * 1.3, 1)
      scene.add(obj)
    })
  } catch (err) {
    console.log('An error happened. ' + err)
  }

  try {
    const loader = new THREE.TextureLoader()
    const texture = await loadPromise(loader, svgData.bgUrl)
    texture.generateMipmaps = false
    texture.minFilter = THREE.LinearFilter
    texture.needsUpdate = true
    const width = texture.image.naturalWidth
    const height = texture.image.naturalHeight

    // Create a 8x8 plane with texture
    const geometry = new THREE.PlaneBufferGeometry(width * 1.3, height * 1.3)
    const material = new THREE.MeshBasicMaterial({ map: texture })
    const plane = new THREE.Mesh(geometry, material)
    plane.scale.set(scale, scale, 1)
    scene.add(plane)
  } catch (err) {
    console.error('An error happened. ' + err)
  }
}
